import React from "react";
import { ContactList } from "./ContactList";
import type { Database } from "@/lib/supabase";
import ScrollArea from "../ui/shared/ScrollArea";
import { useState, useRef, useCallback, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { ChatFilters, type ChatFilterValues, defaultChatFilters } from "./ChatFilters";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

interface ChatSidebarProps {
  contacts: AccountContact[];
  selectedContact: AccountContact | null;
  onContactSelect: (contact: AccountContact) => void;
  loading: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
}

const SidebarHeader = ({
  searchTerm,
  onSearchChange,
  filters,
  onFiltersChange
}: {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  filters: ChatFilterValues;
  onFiltersChange: (filters: ChatFilterValues) => void;
}) => {
  return (
    <div className="flex gap-2">
      <div className="gap-2 rounded-[6px] border border-neutral-500 bg-neutral-600 px-3 flex justify-start items-center flex-1 h-[40px]">
        <svg className="w-[16px] h-[16px] flex-shrink-0" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M6.91382 2.65576L7.15698 2.67822C8.76918 2.87399 10.1267 4.08856 10.5281 5.64697L10.5955 5.96338C10.8047 7.21398 10.4233 8.38828 9.68433 9.25049L9.48511 9.48193L9.25366 9.68115C8.39132 10.4203 7.21639 10.8018 5.96558 10.5923H5.96655C4.36514 10.3215 3.06273 9.04828 2.73413 7.47314L2.6814 7.15381C2.3712 4.63045 4.41754 2.49568 6.91382 2.65576ZM6.65112 3.31396C4.80703 3.31396 3.31714 4.80385 3.31714 6.64795C3.31726 8.49194 4.80711 9.98096 6.65112 9.98096C8.49503 9.98083 9.984 8.49186 9.98413 6.64795C9.98413 4.80393 8.49511 3.31409 6.65112 3.31396ZM9.98413 9.98682L9.89038 9.89014L9.81909 9.81592L9.89331 9.88721L9.98999 9.98096H10.5125L13.241 12.7231H13.2419C13.3851 12.8663 13.3851 13.1024 13.2419 13.2456C13.0988 13.3887 12.8626 13.3887 12.7195 13.2456L9.98413 10.5093V9.98682Z"
            fill="#71717A"
            stroke="#71717A"
            strokeWidth="0.666667"
          />
        </svg>
        <input
          type="text"
          placeholder="Pesquisar contatos..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="grow-1 text-[14px] font-normal text-neutral-50 placeholder:text-neutral-400 w-full h-full border-none focus:outline-none bg-transparent"
        />
      </div>
      <ChatFilters filters={filters} onFiltersChange={onFiltersChange} />
    </div>
  );
};

export const ChatSidebar = ({
  contacts,
  selectedContact,
  onContactSelect,
  loading,
  hasMore = false,
  onLoadMore,
  filters,
  onFiltersChange,
}: ChatSidebarProps) => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<AccountContact[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const searchContacts = useCallback(async (term: string) => {
    if (!user?.account_id || !supabase || !term.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    try {
      setSearchLoading(true);
      setIsSearching(true);

      const searchPattern = `%${term}%`;

      let query = supabase
        .from('account_contacts')
        .select('*')
        .eq('account_id', user.account_id)
        .or(`display_name.ilike.${searchPattern},push_name_last.ilike.${searchPattern},jid.ilike.${searchPattern},phone.ilike.${searchPattern},email.ilike.${searchPattern}`);

      if (filters.attributionId) {
        if (filters.attributionId === '__unassigned__') {
          query = query.is('attribution_id', null);
        } else {
          query = query.eq('attribution_id', filters.attributionId);
        }
      }

      if (filters.statusChat) {
        query = query.eq('status_chat', filters.statusChat);
      }

      if (filters.tag) {
        query = query.contains('tags', [filters.tag]);
      }

      const { data: contactsData, error: contactsError } = await query
        .order('last_message_at', { ascending: false, nullsFirst: false })
        .limit(50);

      if (contactsError) throw contactsError;

      setSearchResults(contactsData || []);
    } catch (err: any) {
      console.error('Erro ao buscar contatos:', err);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [user?.account_id, filters]);

  // Debounce para busca - aguarda 300ms após o usuário parar de digitar
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchTerm.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchContacts(searchTerm);
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchContacts]);

  // Determinar quais contatos mostrar: resultados da busca ou contatos paginados
  const displayedContacts = isSearching ? searchResults : contacts;
  const isLoading = isSearching ? searchLoading : loading;
  const shouldShowInfiniteScroll = !isSearching && hasMore;

  // Intersection Observer para infinite scroll (apenas quando não estiver pesquisando)
  React.useEffect(() => {
    if (!onLoadMore || !shouldShowInfiniteScroll || isLoading) return;

    const currentTarget = observerTarget.current;
    if (!currentTarget) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          console.log('📜 [ChatSidebar] Loading more contacts...');
          onLoadMore();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    observer.observe(currentTarget);

    return () => {
      try {
        if (currentTarget && observer) {
          observer.unobserve(currentTarget);
        }
        observer.disconnect();
      } catch (error) {
        console.warn('⚠️ [ChatSidebar] Error disconnecting observer:', error);
      }
    };
  }, [onLoadMore, shouldShowInfiniteScroll, hasMore, isLoading]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  return (
    <div className=" flex gap-4 flex-col h-full">
      <SidebarHeader
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
      <ScrollArea
        classNames={{
          root: "flex-1 md:block hidden h-full w-full overflow-auto md:max-w-[280px]",
        }}
      >
        <ContactList
          contacts={displayedContacts}
          selectedContact={selectedContact}
          onContactSelect={onContactSelect}
          loading={isLoading}
          searchTerm={searchTerm}
        />
        {shouldShowInfiniteScroll && (
          <div ref={observerTarget} className="py-4 text-center">
            <div className="text-sm text-neutral-400">Carregando mais contatos...</div>
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
